/**
 * siteAssets — shared helpers for the content-addressed images a site uses.
 *
 * Images are content-addressed by sha256 and stored out-of-band from git:
 * locally in OPFS (`sites/<id>/assets/<sha>.<ext>`) and remotely under the
 * served key `_assets/<sha>`. The source JSON only carries the sha. These
 * helpers enumerate the images a site references (from the grid store) so the
 * publish-sync and the "download with history" export can both resolve bytes.
 */
import type { GridState } from "@fredericrous/grid";

/** The served object key for a content-addressed image (no extension). */
export const ASSET_KEY_PREFIX = "_assets/";
export function assetKey(sha256: string): string {
  return `${ASSET_KEY_PREFIX}${sha256}`;
}

export interface ImageAsset {
  readonly sha256: string;
  readonly ext: string;
  readonly mime: string;
}

/** Every distinct content-addressed media asset (image, video, OR audio)
 *  referenced across all grids — the bytes publish must upload. Same content-
 *  addressed pipeline for all three; the mime distinguishes them. */
export function collectImageAssets(
  grids: Readonly<Record<string, GridState>>,
): ImageAsset[] {
  const bySha = new Map<string, ImageAsset>();
  for (const grid of Object.values(grids)) {
    for (const item of Object.values(grid.items)) {
      if (
        item.contentType !== "image" &&
        item.contentType !== "video" &&
        item.contentType !== "audio"
      )
        continue;
      const cp = item.contentProps ?? {};
      const sha256 = typeof cp["sha256"] === "string" ? cp["sha256"] : null;
      if (!sha256 || bySha.has(sha256)) continue;
      bySha.set(sha256, {
        sha256,
        ext: typeof cp["ext"] === "string" ? cp["ext"] : "bin",
        mime:
          typeof cp["mime"] === "string"
            ? cp["mime"]
            : "application/octet-stream",
      });
    }
  }
  return [...bySha.values()];
}
